import React, { useRef } from 'react';
import { ShoppingItem, CategoryInfo } from '../types';
import { Trash2, Plus, Minus, Edit3, Check, Package, Tag, Camera, Loader2, ChevronDown, Sparkles, Radar, X, MapPin, Navigation, ShoppingBag as BagIcon, Clock, Barcode, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { RestockItem } from '../src/lib/restock';
import type { StorePriceChange } from '../src/lib/priceChange';
import { restockDueText, isRestockUrgent } from './common/restockText';
import { useTranslation } from 'react-i18next';
import { useStore } from '../src/lib/store';

import { EditItemModal } from './EditItemModal';
import { GroundedDealsModal } from './GroundedDealsModal';
import { UpgradeToProMessage } from './common/UpgradeToProMessage';
import { GhostPulse, getLatticeItemStatus } from './common/GhostPulse';
import { LatticeBeaterPanel, PriceCheckPanel, LatticeFreshnessBadge } from './common/LatticeStatusPanel';
import { UnitPriceIconButton, UnitPriceCheckPanel, applyLooksRight } from './common/UnitPriceCheckPanel';
import { hasUnitPriceAlert } from '../src/lib/basketItem';
import { ConfirmationModal } from './common/ConfirmationModal';
import { SmartImage } from './common/SmartImage';
import { computePricePerUnit } from '../src/lib/intelligence';
import { LatticeSeedItem, LatticeSeedScope } from '../src/types/latticeSeed';
import { splitStoreName, formatSeedDistance } from '../src/lib/latticeSeedFormat';

interface ShoppingListProps {
  items: ShoppingItem[];
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<ShoppingItem>) => void;
  onImageUpload?: (id: string, file: File | string) => Promise<void>;
  onPreviewImage: (url: string) => void;
  onAddSuggested?: (item: RestockItem) => void;
  onSnoozeSuggested?: (item: RestockItem) => void;
  onHideSuggested?: (item: RestockItem) => void;
  categories: CategoryInfo[];
  /** Restock items due for this trip (selectBasketSuggestions). */
  suggestions?: RestockItem[];
  /** "Price changed at this store" per basket item id (src/lib/priceChange.ts). */
  storePriceChanges?: Record<string, StorePriceChange>;
  /** Whether the user has any restock rhythm yet (empty-state copy). */
  hasRestockItems?: boolean;
  currentStoreName?: string | null;
  minimized?: boolean;
  currencySymbol?: string;
  isUsageLimitReached?: boolean;
  isPersonnelMode?: boolean;
  latticeSeedItems?: LatticeSeedItem[];
  /** Where and how far Trending looked (the header says which centre it used). */
  latticeSeedScope?: LatticeSeedScope | null;
  // Free plan archive allowance: when the next archive frees up.
  archiveNextSlotAt?: string | null;
  archiveLimit?: number;
  archiveWindowDays?: number;
  isLoadingLatticeSeed?: boolean;
  showLatticeSeed?: boolean;
  onSeedItemTap?: (item: LatticeSeedItem) => void;
}

export const ShoppingList: React.FC<ShoppingListProps> = ({
  items, onRemove, onUpdate, onImageUpload, onPreviewImage, onAddSuggested, onSnoozeSuggested, onHideSuggested,
  categories, suggestions = [], storePriceChanges = {}, hasRestockItems = false, currentStoreName = null, minimized, currencySymbol = '$', isUsageLimitReached = false,
  isPersonnelMode = false, latticeSeedItems = [], latticeSeedScope = null, isLoadingLatticeSeed = false,
  archiveNextSlotAt = null, archiveLimit = 3, archiveWindowDays = 30,
  showLatticeSeed = false, onSeedItemTap
}) => {
  const { t, i18n } = useTranslation();
  const { isProUser, isUltraUser, userProfile, pendingItems } = useStore();
  const isUltra = !!isUltraUser || !!userProfile?.is_ultra;

  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);
  const [expandedSavingsId, setExpandedSavingsId] = React.useState<string | null>(null);
  const [expandedUnitCheckId, setExpandedUnitCheckId] = React.useState<string | null>(null);
  const [isEssentialsExpanded, setIsEssentialsExpanded] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState<ShoppingItem | null>(null);
  const [dealsModalItem, setDealsModalItem] = React.useState<ShoppingItem | null>(null);
  // Default straight to Trending when the basket is genuinely empty, so a
  // cold-start user sees real content immediately rather than a blank basket
  // tab; established users with items already in-progress land on Basket.
  const [activeTab, setActiveTab] = React.useState<'basket' | 'trending'>(() =>
    (items.length === 0 && !isUsageLimitReached) ? 'trending' : 'basket'
  );

  const selectedItem = React.useMemo(() =>
    items.find(i => i.id === selectedItemId) || null
  , [items, selectedItemId]);

  // Space kept clear under the list for the fixed AppBottomPanel, which
  // publishes its live height as --bottom-panel-h; the fallbacks cover the
  // first paint before it has measured itself.
  const bottomPanelClearance = `calc(var(--bottom-panel-h, ${minimized ? '140px' : '480px'}) + 16px)`;

  const getCategoryInfo = (name: string) => {
    return categories.find(c => c.name === name) || { color: '#94a3b8', emoji: undefined };
  };

  const tabSwitcher = !isPersonnelMode && showLatticeSeed && (
    <div className="flex justify-center px-1 mb-6">
      <div className="bg-gray-100 p-1.5 rounded-2xl flex items-center gap-1 shadow-inner w-full max-w-xs">
        <button
          onClick={() => setActiveTab('basket')}
          className={`flex-1 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'basket' ? 'bg-white text-[#1a2b3c] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
        >
          {t('basket.tab_label', { defaultValue: 'Basket' })}
        </button>
        <button
          onClick={() => setActiveTab('trending')}
          className={`relative flex-1 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'trending' ? 'bg-white text-[#1a2b3c] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
        >
          {t('lattice_seed.tab_trending', { defaultValue: 'Trending' })}
          {isLoadingLatticeSeed && (
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
            </span>
          )}
        </button>
      </div>
    </div>
  );

  const trendingTierLabel = latticeSeedItems[0]?.tier === 'metro'
    ? t('lattice_seed.tier_metro', { defaultValue: 'Trending in your metro area' })
    : latticeSeedItems[0]?.tier === 'national'
    ? t('lattice_seed.tier_national', { defaultValue: 'Trending Nationally' })
    : t('lattice_seed.section_title', { defaultValue: 'Trending Near You' });

  // The centre Trending measured from, with the distance the list actually used
  // (the metro tier looks 4x as far; national has no centre to name).
  const trendingTier = latticeSeedItems[0]?.tier;
  const trendingMiles = latticeSeedScope && (trendingTier === 'metro' ? latticeSeedScope.radiusMiles * 4 : latticeSeedScope.radiusMiles);
  const trendingCentreLabel = !latticeSeedScope?.source || trendingTier === 'national' || latticeSeedItems.length === 0
    ? null
    : latticeSeedScope.source === 'store' && latticeSeedScope.label
    ? t('lattice_seed.centre_store', { miles: trendingMiles, store: latticeSeedScope.label, defaultValue: 'Within {{miles}} mi of {{store}}' })
    : latticeSeedScope.source === 'zip' && latticeSeedScope.label
    ? t('lattice_seed.centre_zip', { miles: trendingMiles, zip: latticeSeedScope.label, defaultValue: 'Within {{miles}} mi of {{zip}}' })
    : t('lattice_seed.centre_location', { miles: trendingMiles, defaultValue: 'Within {{miles}} mi of you' });

  const trendingGrid = (
    <div className="space-y-4 px-1 animate-in fade-in duration-500">
      <div className="flex items-start justify-between gap-3 px-2">
        <div className="min-w-0">
          <h4 className="text-sm font-black text-[#1a2b3c] tracking-tight flex items-center gap-1.5">
            <Radar size={14} className="text-indigo-500 shrink-0" />
            {trendingTierLabel}
          </h4>
          <p className="text-[11px] font-medium text-gray-400 mt-0.5 leading-snug">
            {t('lattice_seed.section_subtitle', { defaultValue: 'Lowest prices shoppers recently paid' })}
          </p>
          {trendingCentreLabel && (
            <p className="text-[11px] font-bold text-indigo-600 mt-1 leading-snug flex items-center gap-1 min-w-0">
              <MapPin size={11} className="shrink-0" aria-hidden="true" />
              <span className="truncate">{trendingCentreLabel}</span>
            </p>
          )}
        </div>
        {latticeSeedItems.length > 0 && (
          <span className="shrink-0 text-[10px] font-black text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
            {t('lattice_seed.item_count', { count: latticeSeedItems.length, defaultValue: `${latticeSeedItems.length} items` })}
          </span>
        )}
      </div>

      {isLoadingLatticeSeed && latticeSeedItems.length === 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden animate-pulse">
              <div className="w-full aspect-square bg-gray-100" />
              <div className="p-3.5 flex flex-col gap-2">
                <div className="h-3 w-4/5 bg-gray-100 rounded" />
                <div className="h-4 w-1/2 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : latticeSeedItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 px-6">
          <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4 text-indigo-400">
            <Radar size={28} />
          </div>
          <p className="text-sm font-black text-[#1a2b3c]">
            {t('lattice_seed.empty_title', { defaultValue: 'Nothing trending yet' })}
          </p>
          <p className="text-xs font-medium text-gray-400 max-w-[260px] leading-relaxed mt-1">
            {t('lattice_seed.empty_others', { defaultValue: 'When other shoppers near you share their receipts, the best prices they paid show up here.' })}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {latticeSeedItems.map((item) => {
            const store = splitStoreName(item.best_store_name);
            const distance = formatSeedDistance(item.distance_miles, t('basket.mi_unit', { defaultValue: 'mi' }));
            return (
              <button
                type="button"
                key={item.slug}
                onClick={() => onSeedItemTap?.(item)}
                className="text-left bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden flex flex-col hover:border-indigo-200 hover:shadow-md active:scale-[0.97] transition-all"
              >
                <div className="relative w-full aspect-square bg-gray-50 flex items-center justify-center overflow-hidden">
                  {item.image_url ? (
                    <SmartImage imageUrl={item.image_url} className="w-full h-full object-cover" bgClassName="bg-gray-50" />
                  ) : (
                    <BagIcon size={32} className="text-indigo-200" />
                  )}
                  {distance && (
                    <span className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/95 backdrop-blur-sm text-[#1a2b3c] text-[10px] font-black px-2 py-1 rounded-full shadow-sm">
                      <MapPin size={10} className="text-indigo-500" />
                      {distance}
                    </span>
                  )}
                </div>

                <div className="p-3.5 flex flex-col flex-1">
                  <h5 className="font-bold text-[#1a2b3c] text-[13px] leading-snug line-clamp-3 min-h-[3.9em]">{item.display_name}</h5>
                  <div className="mt-2.5 pt-2.5 border-t border-gray-50 flex items-end justify-between gap-2">
                    <span className="text-[#1a2b3c] font-black text-lg leading-none tracking-tight">
                      {currencySymbol}{item.best_price.toFixed(2)}
                    </span>
                    {store.name && (
                      <span className="text-[10px] font-bold text-gray-500 truncate max-w-[60%]">{store.name}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Suggested Essentials: "add to this trip" (due items the user buys at this store).
  const essentialsEmptyText = !hasRestockItems
    ? t('restock.learning', { defaultValue: 'After a few trips, CartLens learns what you rebuy and tells you when it is due.' })
    : currentStoreName
    ? t('restock.none_here', { defaultValue: 'Nothing you usually buy here is due' })
    : t('restock.nothing_due', { defaultValue: 'Nothing due right now' });

  const essentialsShelf = !isPersonnelMode && (
    <div className="mb-8 space-y-3 px-1 animate-in fade-in slide-in-from-top-4 duration-700">
      <div
        onClick={() => setIsEssentialsExpanded(!isEssentialsExpanded)}
        className="flex justify-between items-center px-3 cursor-pointer group/header"
      >
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover/header:text-[#1a2b3c] transition-colors">{t('basket.suggested_essentials')}</h4>
          {suggestions.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-600 text-[8px] font-black">{suggestions.length}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-gray-50 text-gray-400 group-hover:text-emerald-500 group-hover:bg-emerald-50 rounded-lg transition-all active:scale-90">
            <ChevronDown size={14} className={`transition-transform duration-300 ${isEssentialsExpanded ? '' : '-rotate-90'}`} />
          </div>
          <Sparkles size={12} className="text-emerald-400" />
        </div>
      </div>

      <AnimatePresence initial={false}>
        {isEssentialsExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="overflow-hidden"
          >
            {suggestions.length > 0 ? (
              <div className="flex overflow-x-auto gap-3 pb-2 -mx-1 scrollbar-hide snap-x">
                {suggestions.map((item) => {
                  const urgent = isRestockUrgent(item);
                  return (
                    <div
                      key={item.key}
                      className="flex-shrink-0 w-48 snap-start bg-white rounded-[2rem] p-4 border border-gray-100 shadow-sm flex flex-col gap-3 text-left"
                    >
                      <div className="flex justify-between items-start">
                        <div className={`w-12 h-12 rounded-xl overflow-hidden border border-gray-100/50 flex items-center justify-center shrink-0 shadow-inner ${urgent ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-500'}`}>
                          {item.imageUrl ? (
                            <SmartImage imageUrl={item.imageUrl} className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <Radar size={18} />
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => onSnoozeSuggested?.(item)}
                            className="bg-gray-50 text-gray-400 p-1.5 rounded-full hover:bg-amber-50 hover:text-amber-500 transition-colors"
                            title={t('restock.snooze', { defaultValue: 'Snooze for a week' })}
                            aria-label={t('restock.snooze', { defaultValue: 'Snooze for a week' })}
                          >
                            <Clock size={10} strokeWidth={3} />
                          </button>
                          <button
                            onClick={() => onHideSuggested?.(item)}
                            className="bg-gray-50 text-gray-400 p-1.5 rounded-full hover:bg-rose-50 hover:text-rose-500 transition-colors"
                            title={t('restock.hide', { defaultValue: "Don't suggest this" })}
                            aria-label={t('restock.hide', { defaultValue: "Don't suggest this" })}
                          >
                            <X size={10} strokeWidth={3} />
                          </button>
                        </div>
                      </div>

                      <div className="min-w-0">
                        <h5 className="font-black text-[#1a2b3c] text-[11px] truncate uppercase tracking-tight leading-none mb-1">{item.name}</h5>
                        <p className={`text-[8px] font-black uppercase ${urgent ? 'text-rose-500' : 'text-emerald-600'}`}>{restockDueText(t, item)}</p>
                      </div>

                      <div className="pt-2 border-t border-gray-50 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          {item.bestPrice > 0 && (
                            <p className="text-[9px] font-black text-[#1a2b3c] truncate">{t('restock.personal_best_short', { defaultValue: 'Personal Best {{price}}', price: `${currencySymbol}${item.bestPrice.toFixed(2)}` })}</p>
                          )}
                          <p className="text-[8px] font-bold text-gray-400 truncate">
                            {currentStoreName
                              ? t('restock.bought_here', { defaultValue: 'You buy it here' })
                              : t('basket.best_at_label', { store: item.bestStore })}
                          </p>
                        </div>
                        <button
                          onClick={() => onAddSuggested?.(item)}
                          className="shrink-0 bg-emerald-500 text-white p-1.5 rounded-full hover:bg-emerald-600 active:scale-90 transition-all"
                          title={t('basket.add_plus')}
                          aria-label={t('basket.add_plus')}
                        >
                          <Plus size={12} strokeWidth={3} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-6 bg-emerald-50/30 rounded-[2rem] border border-emerald-100/50">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-emerald-500 shadow-sm shrink-0">
                  <Check size={20} strokeWidth={3} />
                </div>
                <p className="text-[10px] font-bold text-emerald-700/70 leading-snug">{essentialsEmptyText}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (items.length === 0 && pendingItems.length === 0) {
    if (isUsageLimitReached) {
      return (
        <div
          className="flex flex-col px-4 transition-all duration-500 ease-in-out"
          style={{ minHeight: `calc(100vh - ${minimized ? '280px' : '540px'})` }}
        >
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12 w-full">
            <div className="w-full max-w-sm animate-in zoom-in duration-500">
              <UpgradeToProMessage
                featureName={t('app.limited_feature_basket')}
                isLimitReached={true}
                targetTier={isProUser ? 'Ultra' : 'Pro'}
              />
              <p className="text-gray-400 mt-6 text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed max-w-[280px] mx-auto opacity-60">
                {archiveNextSlotAt
                  ? t('app.free_archive_locked_desc', {
                      limit: archiveLimit,
                      days: archiveWindowDays,
                      date: new Date(archiveNextSlotAt).toLocaleString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
                      tier: isProUser ? 'Ultra' : 'Pro',
                      defaultValue: "You've archived {{limit}} trips in the last {{days}} days on the free plan. Your next archive unlocks {{date}}, or upgrade to {{tier}} for unlimited trips.",
                    })
                  : t('app.archive_locked_desc', { tier: isProUser ? 'Ultra' : 'Pro' })}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (!showLatticeSeed) {
      return (
        <div
          className="flex flex-col px-4 transition-all duration-500 ease-in-out"
          style={{ minHeight: `calc(100vh - ${minimized ? '280px' : '540px'})` }}
        >
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12 w-full">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4 text-emerald-500 animate-in zoom-in duration-500">
               <BagIcon size={40} />
            </div>
            <h3 className="text-xl font-black text-[#1a2b3c] tracking-tight animate-in fade-in slide-in-from-bottom-2 duration-500 delay-150">{t('basket.empty_title')}</h3>
            <p className="text-gray-400 max-w-[240px] mt-2 text-xs font-bold uppercase tracking-widest leading-relaxed animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
              {t('basket.empty_desc')}
            </p>
          </div>
        </div>
      );
    }

    return (
      <div
        className="flex flex-col px-4 transition-all duration-500 ease-in-out"
        style={{
          minHeight: `calc(100vh - ${minimized ? '280px' : '540px'})`,
          ...(activeTab === 'trending' ? { paddingBottom: bottomPanelClearance } : {}),
        }}
      >
        {tabSwitcher}
        {activeTab === 'trending' ? trendingGrid : (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12 w-full">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-4 text-emerald-500 animate-in zoom-in duration-500">
               <BagIcon size={40} />
            </div>
            <h3 className="text-xl font-black text-[#1a2b3c] tracking-tight animate-in fade-in slide-in-from-bottom-2 duration-500 delay-150">{t('basket.empty_title')}</h3>
            <p className="text-gray-400 max-w-[240px] mt-2 text-xs font-bold uppercase tracking-widest leading-relaxed animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
              {t('basket.empty_desc')}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`space-y-4 px-1 transition-all duration-500 ease-in-out`}
      style={{ paddingBottom: bottomPanelClearance }}
    >
      {tabSwitcher}

      {(!showLatticeSeed || activeTab === 'basket') ? (
        <>
          {essentialsShelf}

          {pendingItems.length > 0 && (
            <div className="space-y-4 mb-4 animate-in fade-in duration-700">
              {/* One card at a time: the photo being analyzed now. Photos still
                  queued (a multi-photo import, quick camera shots) take its
                  place in turn. */}
              {pendingItems.slice(0, 1).map((pending) => (
                <div
                  key={pending.id}
                  className="bg-white/40 backdrop-blur-sm rounded-[2rem] p-4 border border-emerald-500/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col gap-4 relative overflow-hidden group"
                >
                  {/* Scanning Laser Line */}
                  <div className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-emerald-400 to-transparent top-0 animate-[scan_3s_ease-in-out_infinite] z-20 opacity-50" />

                  {/* Subtle Shimmer Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2.5s_infinite]" />

                  <div className="flex items-center gap-3 sm:gap-4 relative z-10">
                    {/* Image Skeleton with Pulse */}
                    <div className="w-20 h-20 flex-shrink-0 bg-[#1a2b3c]/5 rounded-[1.5rem] overflow-hidden border border-[#1a2b3c]/5 flex items-center justify-center relative">
                      {pending.imageUrl ? (
                        <SmartImage imageUrl={pending.imageUrl} className="w-full h-full object-cover rounded-[1.5rem] opacity-30 grayscale blur-[2px] scale-110" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                          <Loader2 className="animate-spin text-emerald-400" size={20} />
                        </div>
                      )}
                      {/* Internal Glow */}
                      <div className="absolute inset-0 shadow-[inset_0_0_20px_rgba(16,185,129,0.1)]" />
                    </div>

                    <div className="flex-1 space-y-3">
                      {/* Category Pill Skeleton */}
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-24 bg-emerald-500/10 rounded-full border border-emerald-500/5 animate-pulse" />
                      </div>

                      {/* Title Skeleton */}
                      <div className="space-y-2">
                        <div className="h-5 w-[85%] bg-slate-100 rounded-lg animate-pulse" />
                        <div className="h-5 w-[40%] bg-slate-100 rounded-lg animate-pulse opacity-60" />
                      </div>

                      {/* Price Skeleton */}
                      <div className="h-7 w-28 bg-emerald-500/5 rounded-xl animate-pulse flex items-center px-3">
                        <div className="h-3 w-16 bg-emerald-200/30 rounded-full" />
                      </div>
                    </div>

                    {/* Right Action Area Skeleton */}
                    <div className="flex flex-col items-end gap-3 opacity-20">
                      <div className="w-14 h-7 rounded-full bg-slate-100" />
                      <div className="flex gap-1">
                        <div className="w-8 h-8 rounded-full bg-slate-100" />
                        <div className="w-8 h-8 rounded-full bg-slate-100" />
                      </div>
                    </div>
                  </div>

                  <style>{`
                    @keyframes shimmer {
                      100% { transform: translateX(100%); }
                    }
                    @keyframes scan {
                      0%, 100% { top: 0%; opacity: 0; }
                      10%, 90% { opacity: 0.5; }
                      50% { top: 100%; }
                    }
                  `}</style>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4">
            {items.map((item) => {
            const catInfo = categories.find(c => c.name === item.category);
            const { color: catColor, emoji: catEmoji } = catInfo || { color: '#94a3b8', emoji: undefined };
            const categoryDisplayName = catInfo?.translationKey ? t(catInfo.translationKey) : item.category;
            const isSavingsExpanded = expandedSavingsId === item.id;
            const latticeStatus = getLatticeItemStatus(item, isUltra);
            const hasValidDeal = latticeStatus === 'deal';
            const savingsPerItem = (hasValidDeal && item.price > 0) ? item.price - item.betterDeal!.price : 0;
            const totalSavings = savingsPerItem * item.quantity;

            return (
              <div
                key={item.id}
                onClick={() => setSelectedItemId(item.id)}
                className="bg-white rounded-[2rem] p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-gray-100 transition-all duration-300 flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-3 cursor-pointer hover:border-emerald-500/30 active:scale-[0.98]"
              >
                {/* The stepper and icons sit on their own full-width row under the
                    name and price, so long names and prices always have room. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-3 sm:gap-x-4">
                  <div className="relative group flex-shrink-0">
                    <div
                      className="w-14 h-14 min-[400px]:w-20 min-[400px]:h-20 flex-shrink-0 bg-gray-50 rounded-[1rem] min-[400px]:rounded-[1.2rem] overflow-hidden border border-gray-100/50 flex items-center justify-center"
                    >
                      {item.imageUrl ? (
                        <SmartImage imageUrl={item.imageUrl} className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-300">
                          <Package size={24} />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span
                        className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex items-center gap-1 border transition-colors truncate max-w-[100px] sm:max-w-none"
                        style={{
                          backgroundColor: `${catColor}15`,
                          color: catColor,
                          borderColor: `${catColor}30`
                        }}
                      >
                        {catEmoji && <span className="text-xs -ml-0.5">{catEmoji}</span>}
                        <Tag size={8} className="shrink-0" /> <span className="truncate">{categoryDisplayName}</span>
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <h4 className="font-bold text-[#1a2b3c] text-base sm:text-lg leading-tight break-normal sm:break-words line-clamp-2">
                          {item.name}
                        </h4>
                        {(item.upc || item.gtin) && (
                          <div className="flex items-center gap-1 self-start px-2 py-0.5 bg-emerald-500 text-white rounded-full shadow-sm shadow-emerald-500/20 shrink-0" title="Sovereign Verified SKU">
                            <Check size={8} strokeWidth={4} />
                            <span className="text-[7px] font-black uppercase tracking-[0.1em]">{t('common.verified')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                      <div className="flex flex-wrap items-baseline gap-x-2 mt-0.5">
                      <p className="text-emerald-600 font-black text-lg min-[400px]:text-xl tracking-tighter">
                        {currencySymbol}{((item.price || 0) * item.quantity).toFixed(2)}
                      </p>
                      {item.quantity > 1 && (
                        <span className="text-[10px] text-gray-400 font-bold">
                          {currencySymbol}{(item.price || 0).toFixed(2)} {t('common.each')}
                        </span>
                      )}
                      {(() => {
                        const ppu = computePricePerUnit(item.price || 0, item.size);
                        return ppu != null && item.unit ? (
                          <span className="text-[10px] text-gray-400 font-bold">
                            ({currencySymbol}{ppu.toFixed(2)}/{item.unit})
                          </span>
                        ) : null;
                      })()}
                      {(() => {
                        const change = storePriceChanges[item.id];
                        if (!change) return null;
                        const up = change.delta > 0;
                        const days = (Date.now() - new Date(change.previousDate).getTime()) / 864e5;
                        const date = new Intl.DateTimeFormat(i18n.language, days < 7 ? { weekday: 'short' } : { month: 'short', day: 'numeric' }).format(new Date(change.previousDate));
                        const amount = `${currencySymbol}${Math.abs(change.delta).toFixed(change.unit && Math.abs(change.delta) < 0.1 ? 3 : 2)}${change.unit ? `/${change.unit}` : ''}`;
                        return (
                          <span
                            className={`px-1.5 py-0.5 rounded-md text-[9px] font-black ${up ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}
                            title={t('price_change.title', { defaultValue: 'You paid {{price}} here last time', price: `${currencySymbol}${change.previousPrice.toFixed(2)}${change.unit ? `/${change.unit}` : ''}` })}
                          >
                            {up
                              ? t('price_change.up', { defaultValue: 'Up {{amount}} since {{date}}', amount, date })
                              : t('price_change.down', { defaultValue: 'Down {{amount}} since {{date}}', amount, date })}
                          </span>
                        );
                      })()}
                      </div>
                    </div>


                  <div className="flex w-full flex-row items-center justify-between gap-2">
                    <div className="flex items-center bg-gray-50 rounded-full p-1 border border-gray-100" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); onUpdate(item.id, { quantity: Math.max(1, item.quantity - 1) }); }}
                        className="p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all text-gray-500"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="w-6 text-center font-black text-xs">{item.quantity}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); onUpdate(item.id, { quantity: item.quantity + 1 }); }}
                        className="p-1.5 hover:bg-white hover:shadow-sm rounded-full transition-all text-emerald-600"
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <div className="flex items-center gap-1">
                      <UnitPriceIconButton
                        item={item}
                        isExpanded={expandedUnitCheckId === item.id}
                        onToggle={() => setExpandedUnitCheckId(expandedUnitCheckId === item.id ? null : item.id)}
                      />
                      {item.canonicalId && (
                        <GhostPulse
                          itemId={item.id}
                          slug={item.canonicalId}
                          currentPrice={item.price}
                          isExpanded={isSavingsExpanded}
                          onToggleExpand={(expanded) => setExpandedSavingsId(expanded ? item.id : null)}
                        />
                      )}
                      {item.metadata?.groundingStatus === 'loading' && (
                        <div
                          className="p-2 text-indigo-400 animate-spin shrink-0"
                          title={t('basket.grounding_loading', { defaultValue: 'Finding Local Prices...' })}
                        >
                          <Loader2 size={14} />
                        </div>
                      )}
                      {(item.metadata?.groundingStatus === 'success' || (item.metadata?.groundedDeals && item.metadata.groundedDeals.length > 0)) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDealsModalItem(item); }}
                          className="p-2 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-all animate-pulse shrink-0"
                          title={t('basket.view_deals', { defaultValue: 'View Local Prices' })}
                        >
                          <Info size={14} />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedItemId(item.id); }}
                        className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-full transition-all"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setItemToDelete(item); }}
                        className="p-2 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded-full transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedUnitCheckId === item.id && hasUnitPriceAlert(item) && (
                    <motion.div
                      key="unit-price-check"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <UnitPriceCheckPanel item={item} currencySymbol={currencySymbol} onLooksRight={() => { applyLooksRight(item, onUpdate); setExpandedUnitCheckId(null); }} />
                    </motion.div>
                  )}
                  {isSavingsExpanded && (latticeStatus === 'beater' || latticeStatus === 'check') && (
                    <motion.div
                      key={latticeStatus}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {latticeStatus === 'beater'
                        ? <LatticeBeaterPanel item={item} currencySymbol={currencySymbol} />
                        : <PriceCheckPanel item={item} currencySymbol={currencySymbol} onUpdate={onUpdate} />}
                    </motion.div>
                  )}
                  {isSavingsExpanded && hasValidDeal && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="bg-indigo-50/50 rounded-2xl p-4 border border-indigo-100/50 mt-2">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-2">
                            <div className="bg-indigo-600 p-1.5 rounded-lg text-white">
                              <Sparkles size={14} />
                            </div>
                            <div>
                              <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{t('basket.best_deal')}</p>
                              <p className="text-sm font-bold text-[#1a2b3c]">{item.betterDeal!.storeName}</p>
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end">
                            <p className="text-sm font-black text-[#1a2b3c] tracking-tighter">
                              {currencySymbol}{(item.betterDeal!.price || 0).toFixed(2)} <span className="text-[9px] text-gray-400 font-bold">{t('common.each')}</span>
                            </p>
                            <div className="mt-1 inline-flex items-center gap-1 bg-emerald-500 text-white px-3 py-1 rounded-xl shadow-sm shadow-emerald-100">
                              <span className="text-[10px] font-black uppercase tracking-widest">{t('common.save')} {currencySymbol}{((item.price - item.betterDeal!.price) * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <a
                            href={(item.betterDeal! as any).latitude && (item.betterDeal! as any).longitude
                              ? `https://www.google.com/maps/search/?api=1&query=${(item.betterDeal! as any).latitude},${(item.betterDeal! as any).longitude}`
                              : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((item.betterDeal!.address || '') + ' ' + item.betterDeal!.storeName)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-start gap-2 group/address"
                          >
                            <MapPin size={12} className="text-gray-400 mt-0.5 shrink-0 group-hover/address:text-indigo-500 transition-colors" />
                            <p className="text-[10px] font-bold text-gray-500 leading-tight group-hover/address:text-indigo-600 group-hover/address:underline transition-all">
                              {item.betterDeal!.address || t('common.unknown_address')}
                            </p>
                          </a>
                          <div className="flex items-center justify-end gap-2">
                            {typeof item.betterDeal!.distance === 'number' && (
                              <div className="flex items-center gap-1 text-indigo-600 bg-indigo-100/50 px-2 py-1 rounded-lg">
                                <Navigation size={10} fill="currentColor" />
                                <span className="text-[9px] font-black uppercase tracking-tighter">{item.betterDeal!.distance.toFixed(1)} mi</span>
                              </div>
                            )}
                            <LatticeFreshnessBadge date={item.betterDeal!.date} reports={item.betterDeal!.reports} />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
          </div>
        </>
      ) : (
        trendingGrid
      )}

      <EditItemModal
        item={selectedItem}
        isOpen={!!selectedItemId}
        onClose={() => setSelectedItemId(null)}
        onUpdate={onUpdate}
        onRemove={onRemove}
        categories={categories}
        currencySymbol={currencySymbol}
        onImageUpload={onImageUpload}
      />

      <ConfirmationModal
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={() => itemToDelete && onRemove(itemToDelete.id)}
        title={t('product.remove_item_title')}
        message={t('product.remove_item_desc', { name: itemToDelete?.name })}
        confirmLabel={t('product.yes_remove')}
        cancelLabel={t('product.keep_item')}
        variant="danger"
      />

      <GroundedDealsModal
        item={dealsModalItem}
        isOpen={!!dealsModalItem}
        onClose={() => setDealsModalItem(null)}
        currencySymbol={currencySymbol}
      />
    </div>
  );
};

const ShoppingBag = ({ size, className }: { size: number, className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
    <path d="M3 6h18" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);
